(function () {
  'use strict';

  class RRGGallery {
    constructor(container) {
      this.container = container;
      this.thumbs = container.querySelectorAll('.rrg-thumb');
      this.slides = container.querySelectorAll('.rrg-main-slide');
      this.mainImage = container.querySelector('.rrg-main-image');
      const mvMapEl = container.querySelector('[data-rrg-media-variant-map]');
      this.mediaVariantMap = mvMapEl ? JSON.parse(mvMapEl.textContent) : {};
      this.init();
    }

    init() {
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const index = parseInt(thumb.dataset.index, 10);
          this.setActive(index);
          const variantId = this.mediaVariantMap[String(index)];
          if (variantId) {
            this.container.dispatchEvent(new CustomEvent('rrg:gallery:select-variant', {
              bubbles: false,
              detail: { variantId }
            }));
          }
        });
      });
    }

    setActive(index) {
      const currentSlide = this.container.querySelector('.rrg-main-slide.is-active');
      if (currentSlide) {
        const currentVideo = currentSlide.querySelector('video');
        if (currentVideo) { currentVideo.pause(); currentVideo.currentTime = 0; }
      }

      this.thumbs.forEach((t) => t.classList.remove('is-active'));
      this.slides.forEach((s) => s.classList.remove('is-active'));
      const thumb = this.container.querySelector(`.rrg-thumb[data-index="${index}"]`);
      const slide = this.container.querySelector(`.rrg-main-slide[data-index="${index}"]`);
      if (thumb) {
        thumb.classList.add('is-active');
        thumb.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      if (slide) {
        slide.classList.add('is-active');
        if (this.mainImage && slide.dataset.ratio) {
          this.mainImage.style.aspectRatio = slide.dataset.ratio;
        }
        const video = slide.querySelector('video');
        if (video) video.play().catch(() => {});
      }
    }
  }

  class RRGVariantPicker {
    constructor(container, gallery) {
      this.container = container;
      this.gallery = gallery;
      this.variantData = JSON.parse(
        container.querySelector('[data-rrg-variants]').textContent
      );
      const mapEl = container.querySelector('[data-rrg-image-media-map]');
      this.imageMediaMap = mapEl ? JSON.parse(mapEl.textContent) : {};
      this.currentOptions = [];
      this.isGlove = container.dataset.isGlove === 'true';
      this.showNotifyFlow = container.dataset.showNotifyFlow === 'true';
      this.hasNotifyMe = !!container.querySelector('#rrg-notify-wrap');
      this.init();
    }

    _selectPrompt() {
      if (!this.isGlove) return 'Select options';
      return this.showNotifyFlow ? 'Select a size to get notified.' : 'Select a size to continue.';
    }

    init() {
      this.container.querySelectorAll('.rrg-option').forEach((optionGroup) => {
        const active = optionGroup.querySelector('.rrg-option__btn.is-selected');
        if (active) {
          const pos = parseInt(active.dataset.optionPosition, 10) - 1;
          this.currentOptions[pos] = active.dataset.value;
        }
      });

      if (this.isGlove) {
        this._applyGloveDefaults();
      }

      document.addEventListener('rrg:rc:selected', () => this.updateVariant());

      this.container.addEventListener('rrg:gallery:select-variant', (e) => {
        const variant = this.variantData.find(v => v.id === e.detail.variantId);
        if (!variant) return;
        const pos = this._findImageOptionPos();
        const value = variant[`option${pos}`];
        const btn = [...this.container.querySelectorAll(`.rrg-option__btn[data-option-position="${pos}"]`)]
          .find(b => b.dataset.value === value);
        if (btn && !btn.classList.contains('is-selected')) btn.click();
      });

      this.container.querySelectorAll('.rrg-option__btn').forEach((btn) => {
        btn.addEventListener('click', () => {
          if (btn.classList.contains('is-unavailable')) return;

          const pos = parseInt(btn.dataset.optionPosition, 10) - 1;
          const value = btn.dataset.value;

          const siblings = this.container.querySelectorAll(
            `.rrg-option__btn[data-option-position="${btn.dataset.optionPosition}"]`
          );
          siblings.forEach((s) => s.classList.remove('is-selected'));
          btn.classList.add('is-selected');

          this.currentOptions[pos] = value;
          this.updateAvailability();
          this.updateVariant();
          this._updateFitHint();
        });
      });

      this.updateAvailability();
      this._updateFitHint();

      if (this.currentOptions.some(v => v != null)) {
        this.updateVariant();
      }
    }

    _applyGloveDefaults() {
      const hasVariantParam = new URLSearchParams(window.location.search).has('variant');
      this.container.querySelectorAll('.rrg-option').forEach((optionGroup) => {
        const buttons = optionGroup.querySelectorAll('.rrg-option__btn');
        if (!buttons.length) return;

        const pos = parseInt(buttons[0].dataset.optionPosition, 10) - 1;
        if (hasVariantParam && this.currentOptions[pos] != null) return;

        const optType = optionGroup.dataset.optionType;
        if (optType !== 'hand' && optType !== 'fit') return;

        const defaultBtn = [...buttons].find(
          btn => btn.dataset.gloveDefault === 'true' && !btn.classList.contains('is-unavailable')
        );

        if (defaultBtn) {
          const siblings = this.container.querySelectorAll(
            `.rrg-option__btn[data-option-position="${defaultBtn.dataset.optionPosition}"]`
          );
          siblings.forEach((s) => s.classList.remove('is-selected'));
          defaultBtn.classList.add('is-selected');
          this.currentOptions[pos] = defaultBtn.dataset.value;
        }
      });
    }

    updateAvailability() {
      this.container.querySelectorAll('.rrg-option__btn').forEach((btn) => {
        const pos = parseInt(btn.dataset.optionPosition, 10) - 1;
        const value = btn.dataset.value;

        const matchesOtherSelections = (v) => {
          for (let i = 0; i < this.currentOptions.length; i++) {
            if (i === pos) continue;
            if (this.currentOptions[i] != null && v[`option${i + 1}`] !== this.currentOptions[i]) {
              return false;
            }
          }
          return true;
        };

        const matching = this.variantData.filter(
          (v) => v[`option${pos + 1}`] === value && matchesOtherSelections(v)
        );
        const exists = matching.length > 0;
        const available = matching.some((v) => v.available);

        btn.classList.toggle('is-unavailable', !exists);
        btn.classList.toggle('is-soldout', exists && !available);
      });
    }

    _findImageOptionPos() {
      const groups = {};
      this.variantData.forEach(v => {
        if (!v.featured_image) return;
        const id = v.featured_image.id;
        if (!groups[id]) groups[id] = [];
        groups[id].push(v);
      });
      const ids = Object.keys(groups);
      if (!ids.length) return 1;
      for (let pos = 1; pos <= 3; pos++) {
        const key = `option${pos}`;
        if (ids.every(id => { const g = groups[id]; return g.every(v => v[key] === g[0][key]); })) return pos;
      }
      return 1;
    }

    findVariant() {
      return this.variantData.find((v) => {
        for (let i = 0; i < this.currentOptions.length; i++) {
          if (v[`option${i + 1}`] !== this.currentOptions[i]) return false;
        }
        return true;
      });
    }

    findBestVariant() {
      return this.variantData.find((v) => {
        for (let i = 0; i < this.currentOptions.length; i++) {
          if (this.currentOptions[i] == null) continue;
          if (v[`option${i + 1}`] !== this.currentOptions[i]) return false;
        }
        return true;
      });
    }

    updateVariant() {
      const optionCount = this.container.querySelectorAll('.rrg-option').length;
      let allSelected = true;
      for (let i = 0; i < optionCount; i++) {
        if (this.currentOptions[i] == null) { allSelected = false; break; }
      }

      if (this.gallery) {
        const galleryVariant = this.findBestVariant();
        if (galleryVariant && galleryVariant.featured_image) {
          const mediaIndex = this.imageMediaMap[String(galleryVariant.featured_image.id)];
          if (mediaIndex !== undefined) this.gallery.setActive(mediaIndex);
        }
      }

      const atcBtn = this.container.querySelector('.rrg-atc-btn');
      const idInput = this.container.querySelector('[name="id"]');

      if (!allSelected) {
        if (atcBtn) {
          atcBtn.hidden = false;
          atcBtn.disabled = true;
          atcBtn.classList.add('is-unavailable');
          atcBtn.textContent = this._selectPrompt();
        }
        if (idInput) idInput.value = '';
        this._toggleNotifyMe(false);
        return;
      }

      const variant = this.findVariant();
      if (!variant) return;

      if (idInput) idInput.value = variant.id;

      const priceEl = this.container.querySelector('.rrg-price-amount');
      if (priceEl) {
        priceEl.style.opacity = '0';
        priceEl.style.transform = 'translateY(4px)';
        requestAnimationFrame(() => {
          priceEl.textContent = this.formatMoney(variant.price);
          requestAnimationFrame(() => {
            priceEl.style.opacity = '1';
            priceEl.style.transform = 'translateY(0)';
          });
        });
      }

      const compareEl = this.container.querySelector('.rrg-compare-price');
      if (compareEl) {
        if (variant.compare_at_price > variant.price) {
          compareEl.textContent = this.formatMoney(variant.compare_at_price);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }

      const afterpayEl = this.container.querySelector('.rrg-afterpay-amount');
      if (afterpayEl) {
        afterpayEl.textContent = this.formatMoney(Math.ceil(variant.price / 4));
      }

      const invWrapper = this.container.querySelector('.rrg-inventory-status-wrapper');
      const invDataEl = this.container.querySelector('[data-rrg-inventory]');
      if (invWrapper && invDataEl) {
        const invData = JSON.parse(invDataEl.textContent);
        const variantInv = invData.variants[String(variant.id)];
        if (variantInv && variantInv.managed === 'shopify' && variantInv.qty > 0 && variantInv.qty <= invData.threshold) {
          invWrapper.innerHTML = '<p class="rrg-inventory-status">' + invData.message.replace('{count}', variantInv.qty) + '</p>';
        } else {
          invWrapper.innerHTML = '';
        }
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      document.dispatchEvent(
        new CustomEvent('variant:change', {
          detail: { variant },
          bubbles: true,
        })
      );

      const rcWidget = document.querySelector('.rc_widget');
      if (rcWidget && !rcWidget.dataset.rcSelected) {
        if (atcBtn) {
          atcBtn.hidden = false;
          atcBtn.disabled = true;
          atcBtn.classList.add('is-unavailable');
          atcBtn.textContent = this._selectPrompt();
        }
        this._toggleNotifyMe(false);
        return;
      }

      const notifyEligible = !variant.available && this.hasNotifyMe;
      if (atcBtn) {
        atcBtn.hidden = false;
        if (variant.available) {
          atcBtn.type = 'submit';
          atcBtn.disabled = false;
          atcBtn.classList.remove('is-unavailable');
          delete atcBtn.dataset.notifyTrigger;
          atcBtn.textContent = 'Add to Cart';
        } else if (notifyEligible) {
          atcBtn.type = 'button';
          atcBtn.disabled = false;
          atcBtn.classList.remove('is-unavailable');
          atcBtn.dataset.notifyTrigger = 'true';
          atcBtn.textContent = 'Notify Me When Available';
        } else {
          atcBtn.type = 'submit';
          atcBtn.disabled = true;
          atcBtn.classList.add('is-unavailable');
          delete atcBtn.dataset.notifyTrigger;
          atcBtn.textContent = 'Sold Out';
        }
      }

      const rcWidgetToggle = document.querySelector('.rc_widget');
      if (rcWidgetToggle) rcWidgetToggle.hidden = !variant.available;

      this._toggleNotifyMe(notifyEligible, variant.id);
    }

    _toggleNotifyMe(eligible, variantId) {
      const notifyWrap = this.container.querySelector('#rrg-notify-wrap');
      if (!notifyWrap) return;
      const variantChanged = variantId != null && notifyWrap.dataset.variantId !== String(variantId);
      if (variantId) notifyWrap.dataset.variantId = variantId;
      if (!eligible || variantChanged) {
        notifyWrap.hidden = true;
        const form = notifyWrap.querySelector('#rrg-notify-form');
        const message = notifyWrap.querySelector('#rrg-notify-message');
        if (form) { form.hidden = true; form.reset(); }
        if (message) message.hidden = true;
      }
    }

    _updateFitHint() {
      const hintEl = this.container.querySelector('.rrg-fit-cadet-hint');
      if (!hintEl) return;
      const isCadet = this.currentOptions.some((v) => v && v.toLowerCase().includes('cadet'));
      hintEl.hidden = !isCadet;
    }

    formatMoney(cents) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  class RRGLightbox {
    constructor(container) {
      this.container = container;
      this.lightbox = document.getElementById('rrg-lightbox');
      if (!this.lightbox) return;
      this.img      = this.lightbox.querySelector('.rrg-lightbox__img');
      this.viewport = this.lightbox.querySelector('.rrg-lightbox__viewport');
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.init();
    }

    init() {
      const zoomBtn = this.container.querySelector('.rrg-zoom-btn');
      if (zoomBtn) zoomBtn.addEventListener('click', () => this.open());

      this.lightbox.addEventListener('click', (e) => {
        if (!e.target.closest('.rrg-lightbox__frame')) this.close();
      });
      this.lightbox.querySelector('.rrg-lightbox__close').addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });

      this.viewport.addEventListener('wheel', (e) => {
        e.preventDefault();
        this.zoomTo(this.scale * (e.deltaY < 0 ? 1.15 : 0.87));
      }, { passive: false });

      this.viewport.addEventListener('click', () => {
        if (this._wasDrag) { this._wasDrag = false; return; }
        this.scale > 1 ? this.resetZoom() : this.zoomTo(2.5);
      });

      this.viewport.addEventListener('mousemove', (e) => {
        if (this.scale <= 1) return;
        const rect = this.viewport.getBoundingClientRect();
        const nx = (e.clientX - rect.left) / rect.width  - 0.5;
        const ny = (e.clientY - rect.top)  / rect.height - 0.5;
        this.tx = -nx * (this.scale - 1) * rect.width;
        this.ty = -ny * (this.scale - 1) * rect.height;
        this.applyTransform(false);
      });

      let t0x = 0, t0y = 0, startTx = 0, startTy = 0;
      let pinchDist = 0;
      this._wasDrag = false;
      const THRESHOLD = 6;

      this.viewport.addEventListener('touchstart', (e) => {
        if (e.touches.length === 1) {
          t0x = e.touches[0].clientX;
          t0y = e.touches[0].clientY;
          startTx = this.tx;
          startTy = this.ty;
          this._wasDrag = false;
        } else if (e.touches.length === 2) {
          pinchDist = Math.hypot(
            e.touches[0].clientX - e.touches[1].clientX,
            e.touches[0].clientY - e.touches[1].clientY
          );
        }
      }, { passive: true });

      this.viewport.addEventListener('touchmove', (e) => {
        if (e.touches.length === 2) {
          e.preventDefault();
          const dist = Math.hypot(
            e.touches[0].clientX - e.touches[1].clientX,
            e.touches[0].clientY - e.touches[1].clientY
          );
          if (pinchDist) this.zoomTo(this.scale * (dist / pinchDist));
          pinchDist = dist;
          return;
        }
        if (e.touches.length !== 1 || this.scale <= 1) return;
        const dx = e.touches[0].clientX - t0x;
        const dy = e.touches[0].clientY - t0y;
        if (!this._wasDrag && (Math.abs(dx) > THRESHOLD || Math.abs(dy) > THRESHOLD)) {
          this._wasDrag = true;
        }
        if (!this._wasDrag) return;
        e.preventDefault();
        const rect = this.viewport.getBoundingClientRect();
        const maxTx = (this.scale - 1) * rect.width  / 2;
        const maxTy = (this.scale - 1) * rect.height / 2;
        this.tx = Math.max(-maxTx, Math.min(maxTx, startTx + dx));
        this.ty = Math.max(-maxTy, Math.min(maxTy, startTy + dy));
        this.applyTransform(false);
      }, { passive: false });
    }

    zoomTo(newScale) {
      this.scale = Math.min(Math.max(1, newScale), 5);
      if (this.scale === 1) { this.tx = 0; this.ty = 0; }
      this.applyTransform(true);
    }

    applyTransform(animated) {
      this.img.style.transition = animated ? 'transform 0.2s ease' : 'none';
      this.img.style.transform  = `scale(${this.scale}) translate(${this.tx / this.scale}px, ${this.ty / this.scale}px)`;
      this.viewport.classList.toggle('is-zoomed', this.scale > 1);
      this.viewport.style.touchAction = this.scale > 1 ? 'none' : 'auto';
    }

    resetZoom() {
      this.scale = 1; this.tx = 0; this.ty = 0;
      this.applyTransform(true);
    }

    open() {
      const activeSlide = this.container.querySelector('.rrg-main-slide.is-active');
      if (!activeSlide) return;
      const imgEl = activeSlide.querySelector('img');
      const newSrc = activeSlide.dataset.full || (imgEl ? imgEl.src : '');
      const newAlt = imgEl ? imgEl.alt : '';
      this.resetZoom();
      this.img.style.transition = 'none';
      this.img.style.opacity = '0';
      this.img.onload = () => {
        this.img.style.transition = 'opacity 0.15s ease';
        this.img.style.opacity = '1';
        this.img.onload = null;
      };
      this.img.src = newSrc;
      this.img.alt = newAlt;
      this.lightbox.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.lightbox.classList.remove('is-open');
      document.body.style.overflow = '';
      this.resetZoom();
    }
  }

  class RRGQuantity {
    constructor(container) {
      this.container = container;
      this.qty = 1;
      this.display = container.querySelector('.rrg-qty-display');
      this.input = container.querySelector('[name="quantity"]');
      this.init();
    }

    init() {
      const minus = this.container.querySelector('.rrg-qty-minus');
      const plus = this.container.querySelector('.rrg-qty-plus');
      if (minus) minus.addEventListener('click', () => this.change(-1));
      if (plus) plus.addEventListener('click', () => this.change(1));
    }

    change(delta) {
      this.qty = Math.max(1, this.qty + delta);
      if (this.display) {
        const animClass = delta > 0 ? 'anim-up' : 'anim-down';
        this.display.classList.remove('anim-up', 'anim-down');
        this.display.textContent = this.qty;
        void this.display.offsetWidth;
        this.display.classList.add(animClass);
      }
      if (this.input) this.input.value = this.qty;
    }
  }

  function initRRGFrequencyPersist() {
    var savedFreq = null;

    document.addEventListener('change', function(e) {
      if (e.target.closest('.rrg-rc-frequency')) {
        savedFreq = e.target.value;
      }
    });

    document.addEventListener('variant:change', function() {
      if (!savedFreq) return;
      var select = document.querySelector('.rrg-rc-frequency select');
      if (!select) return;

      if (select.value !== savedFreq) {
        select.value = savedFreq;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      var observer = new MutationObserver(function() {
        if (select.value !== savedFreq) {
          select.value = savedFreq;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      observer.observe(select, { attributes: true, childList: true, subtree: true });
      setTimeout(function() { observer.disconnect(); }, 2000);
    });
  }

  function initRRGATCFeedback(container) {
    const form   = container.querySelector('.shopify-product-form');
    const atcBtn = container.querySelector('.rrg-atc-btn');
    if (!form || !atcBtn) return;

    form.addEventListener('submit', () => {
      if (atcBtn.disabled || atcBtn.classList.contains('is-loading')) return;
      atcBtn.classList.add('is-loading');
      atcBtn.textContent = 'Adding...';

      let fallback;

      const onDone = () => {
        clearTimeout(fallback);
        atcBtn.classList.remove('is-loading');
        atcBtn.classList.add('is-added');
        atcBtn.textContent = 'Added';
        setTimeout(() => {
          atcBtn.classList.remove('is-added');
          atcBtn.textContent = 'Add to Cart';
        }, 1500);
      };

      document.addEventListener('rrg:atc:done', onDone, { once: true });

      fallback = setTimeout(() => {
        document.removeEventListener('rrg:atc:done', onDone);
        atcBtn.classList.remove('is-loading');
        atcBtn.textContent = 'Add to Cart';
      }, 8000);
    });
  }

  function initRRGNotifyMe(container) {
    const wrap = container.querySelector('#rrg-notify-wrap');
    if (!wrap) return;

    const form = wrap.querySelector('#rrg-notify-form');
    const submitBtn = wrap.querySelector('#rrg-notify-submit');
    const message = wrap.querySelector('#rrg-notify-message');
    const emailInput = form.querySelector('input[name="email"]');

    container.addEventListener('click', (e) => {
      const atcBtn = e.target.closest('#rrg-add-to-cart');
      if (!atcBtn || atcBtn.dataset.notifyTrigger !== 'true') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      wrap.hidden = false;
      form.hidden = false;
      message.hidden = true;
      emailInput.focus();
    }, true);

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      const email = emailInput.value.trim();
      const variantId = wrap.dataset.variantId;
      const companyId = wrap.dataset.klaviyoCompanyId;
      if (!email || !variantId || !companyId) return;

      submitBtn.disabled = true;
      submitBtn.textContent = 'Submitting...';

      fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2024-10-15',
        },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              channels: ['EMAIL'],
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email },
                },
              },
            },
            relationships: {
              variant: {
                data: {
                  type: 'catalog-variant',
                  id: `$shopify:::$default:::${variantId}`,
                },
              },
            },
          },
        }),
      })
        .then((res) => {
          if (res.status !== 202) throw new Error('bad status');
          form.hidden = true;
          message.textContent = "You're on the list! We'll email you when this is back in stock.";
          message.classList.remove('is-error');
          message.hidden = false;
        })
        .catch(() => {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Notify Me';
          message.textContent = 'Something went wrong. Please try again.';
          message.classList.add('is-error');
          message.hidden = false;
        });
    });
  }

  function initRRG3DModal() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('.product-3d-button');
      const modal = document.querySelector('.product-3d-modal');

      if (btn && modal) {
        const spinUrl = btn.getAttribute('data-spin');
        const viewer = modal.querySelector('.product-3d-viewer');
        viewer.innerHTML = `<div class="Sirv" data-src="${spinUrl}"></div>`;
        modal.hidden = false;
        document.body.style.overflow = 'hidden';
        if (window.Sirv && typeof window.Sirv.start === 'function') window.Sirv.start();
        return;
      }

      const shouldClose = modal && !modal.hidden && (
        e.target.closest('[data-3d-close]') ||
        (!e.target.closest('.product-3d-dialog') && e.target.closest('.product-3d-modal'))
      );
      if (shouldClose) {
        modal.hidden = true;
        document.body.style.overflow = '';
        const viewer = modal.querySelector('.product-3d-viewer');
        if (viewer) viewer.innerHTML = '';
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const modal = document.querySelector('.product-3d-modal');
      if (modal && !modal.hidden) {
        modal.hidden = true;
        document.body.style.overflow = '';
        const viewer = modal.querySelector('.product-3d-viewer');
        if (viewer) viewer.innerHTML = '';
      }
    });
  }

  function initRRGProduct() {
    const container = document.querySelector('.rrg-product-container');
    if (!container) return;

    const gallery = new RRGGallery(container);
    new RRGLightbox(container);
    if (container.querySelector('[data-rrg-variants]')) {
      new RRGVariantPicker(container, gallery);
    }
    new RRGQuantity(container);
    initRRGATCFeedback(container);
    initRRGNotifyMe(container);
    initRRGFrequencyPersist();
    initRRG3DModal();
  }

  function initRRGAccordionPosition() {
    var accordions = document.querySelector('.rrg-accordions');
    var related = document.querySelector('.rrg-related');
    var gallery = document.querySelector('.rrg-gallery');
    var product = document.querySelector('.rrg-product');
    if (!accordions || !gallery || !product) return;

    function reposition() {
      if (window.innerWidth < 750) {
        if (accordions.parentElement !== product) {
          product.appendChild(accordions);
        }
        if (related && related.parentElement !== product) {
          product.appendChild(related);
        }
      } else {
        if (accordions.parentElement !== gallery) {
          gallery.appendChild(accordions);
        }
        if (related && related.parentElement !== gallery) {
          gallery.appendChild(related);
        }
      }
    }

    reposition();
    window.addEventListener('resize', reposition);
  }

  function initRRGReadMore() {
    document.querySelectorAll('a[href="#rrg-description"]').forEach(function(link) {
      link.addEventListener('click', function(e) {
        e.preventDefault();
        var target = document.getElementById('rrg-description');
        if (!target) return;
        target.open = true;
        var header = document.querySelector('sticky-header, .shopify-section-header-sticky, header-group');
        var headerHeight = header ? header.offsetHeight : 0;
        var top = target.getBoundingClientRect().top + window.pageYOffset - headerHeight - 16;
        window.scrollTo({ top: top, behavior: 'smooth' });
      });
    });
  }

  function initRRGNotifyBadgeScroll() {
    var link = document.getElementById('rrg-notify-badge-link');
    if (!link) return;
    link.addEventListener('click', function(e) {
      e.preventDefault();
      var target = document.getElementById('rrg-options-start') || document.getElementById('rrg-add-to-cart');
      if (!target) return;
      var header = document.querySelector('sticky-header, .shopify-section-header-sticky, header-group');
      var headerHeight = header ? header.offsetHeight : 0;
      var top = target.getBoundingClientRect().top + window.pageYOffset - headerHeight - 16;
      window.scrollTo({ top: top, behavior: 'smooth' });
    });
  }

  function initRRGRelated() {
    var container = document.querySelector('.rrg-related');
    if (!container) return;
    var productId = container.dataset.productId;
    if (!productId) return;

    var currentGender = container.dataset.gender || '';

    function getProductGender(tags) {
      if (!tags) return '';
      var list = (typeof tags === 'string' ? tags.split(',') : tags)
        .map(function(t) { return t.trim().toLowerCase(); });
      if (list.some(function(t) { return t === 'women' || t === 'womens' || t === "women's" || t === 'female' || t === 'woman'; })) return 'women';
      if (list.some(function(t) { return t === 'men' || t === 'mens' || t === "men's" || t === 'male' || t === 'man'; })) return 'men';
      return '';
    }

    fetch('/recommendations/products.json?product_id=' + productId + '&limit=10&intent=related')
      .then(function(r) { return r.json(); })
      .then(function(data) {
        var products = data.products;
        if (!products || !products.length) return;

        var filtered = products;
        if (currentGender) {
          var oppositeGender = currentGender === 'men' ? 'women' : 'men';
          filtered = products.filter(function(p) {
            return getProductGender(p.tags) !== oppositeGender;
          });
        }

        filtered = filtered.slice(0, 4);
        if (!filtered.length) return;

        function fmtMoney(cents) {
          return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
        }

        var heading = document.createElement('p');
        heading.className = 'rrg-related__heading';
        heading.textContent = 'You May Also Like';
        container.appendChild(heading);

        var grid = document.createElement('div');
        grid.className = 'rrg-related__grid';

        filtered.forEach(function(p) {
          var imgSrc = p.featured_image
            ? p.featured_image.replace(/(\.\w+)$/, '_400x400$1')
            : '';
          var card = document.createElement('a');
          card.className = 'rrg-related__card';
          card.href = p.url;
          card.innerHTML =
            '<div class="rrg-related__img">' +
              (imgSrc ? '<img src="' + imgSrc + '" alt="' + p.title.replace(/"/g, '&quot;') + '" loading="lazy">' : '') +
            '</div>' +
            '<p class="rrg-related__title">' + p.title + '</p>' +
            '<p class="rrg-related__price">' + fmtMoney(p.price_min) + '</p>';
          grid.appendChild(card);
        });

        container.appendChild(grid);
      })
      .catch(function() {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      initRRGProduct();
      initRRGAccordionPosition();
      initRRGReadMore();
      initRRGNotifyBadgeScroll();
      initRRGRelated();
    });
  } else {
    initRRGProduct();
    initRRGAccordionPosition();
    initRRGReadMore();
    initRRGNotifyBadgeScroll();
    initRRGRelated();
  }
})();
